import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { ODataField, ODataFieldType } from '@nimbus-global/features/shared/models/api/odata/o-data-field.model';
import { ODataRequest } from '@nimbus-global/features/shared/models/api/odata/o-data-request';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { IContact } from '@nimbus-library/features/shared/models/contacts/i-contact.model';
import { SearchExpressionQueryIds } from '@nimbus-library/features/graph/models/search-expression-query-factories.model';
import { SearchParserResult } from '@nimbus-library/features/graph/models/search-parser-result.model';
import { graphFieldModifiers, GraphFilter } from '@nimbus-library/features/graph/models/graph-filter.model';
import { OrganizationUnitService } from '@nimbus-global/features/shared/services/api/organization-unit/organization-unit.service';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import { ContactsApiRoutes } from '@nimbus-library/features/shared/services/contacts-odata/domain/api-routes.const';
import { ContactsDataProvider } from '@nimbus-library/features/shared/services/contacts-odata/models/contacts-data-provider';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { ContactsMapper } from '../contacts.mapper';

export const customContactsUserSearchOptions: IMap<ODataField> = {
  /// Primitives
  FirstName: {
    name: 'FirstName',
    type: ODataFieldType.Primitive,
  },
  LastName: {
    name: 'LastName',
    type: ODataFieldType.Primitive,
  },
  DisplayName: {
    name: 'DisplayName',
    type: ODataFieldType.Primitive,
  },
  Department: {
    name: 'Department',
    type: ODataFieldType.Primitive,
  },
  Initials: {
    name: 'Initials',
    type: ODataFieldType.Primitive,
  },
  Company: {
    name: 'Company',
    type: ODataFieldType.Primitive,
  },
  JobTitle: {
    name: 'JobTitle',
    type: ODataFieldType.Primitive,
  },
  UserPrincipalName: {
    name: 'UserPrincipalName',
    type: ODataFieldType.Primitive,
  },

  // Primitive Arrays
  ImAddresses: {
    name: 'ImAddresses',
    type: ODataFieldType.ArrayPrimitive,
  },
  EmailAddresses: {
    name: 'EmailAddresses',
    type: ODataFieldType.ArrayPrimitive,
  },
  BusinessPhones: {
    name: 'BusinessPhones',
    type: ODataFieldType.ArrayPrimitive,
  },
  HomePhones: {
    name: 'HomePhones',
    type: ODataFieldType.ArrayPrimitive,
  },
  MobilePhones: {
    name: 'MobilePhones',
    type: ODataFieldType.ArrayPrimitive,
  },

  // Array of objects
  'addresses.street': {
    name: 'street',
    root: 'addresses',
    type: ODataFieldType.Array,
  },
  'addresses.city': {
    name: 'city',
    root: 'addresses',
    type: ODataFieldType.Array,
  },
  'addresses.country': {
    name: 'country',
    root: 'addresses',
    type: ODataFieldType.Array,
  },
  'addresses.state': {
    name: 'state',
    root: 'addresses',
    type: ODataFieldType.Array,
  },
  'addresses.postalCode': {
    name: 'postalCode',
    root: 'addresses',
    type: ODataFieldType.Array,
  },
};

export const customContactSearchQueryFactories: SearchExpressionQueryIds = {
  city: 'addresses.city',
  company: 'Company',
  country: 'addresses.country',
  department: 'Department',
  jobTitle: 'JobTitle',
  initials: 'Initials',
  postalCode: 'addresses.postalCode',
  state: 'addresses.state',
  streetAddress: 'addresses.street',
};

@Injectable({ providedIn: 'root' })
export class ContactsODataService {
  private readonly defaultPageSize = 200;
  private readonly defaultSortExpression = 'DisplayName asc';
  private readonly mapper: ContactsMapper = new ContactsMapper();

  public constructor(
    private readonly baseHttpService: BaseHttpService,
    private readonly logService: ILogger,
    private readonly organizationUnitService: OrganizationUnitService,
    private readonly authService: IAuthenticationService,
  ) {}

  public getContactsByQuery(
    searchExpression?: SearchParserResult,
    sortExpression?: string,
    pageSize?: number,
    tenantId?: string,
    userId?: string,
  ): ContactsDataProvider {
    const userInfo = this.authService.getUserInfo();
    const searchFilter = searchExpression ? this.composeCustomFilters(searchExpression, customContactSearchQueryFactories) : null;
    const searchFilterText = searchFilter.length > 0 ? `${searchFilter.filter((f) => !!f).join(' and ')}` : null;
    const request = new ODataRequest(
      ContactsApiRoutes.Contacts.SearchByUserId(tenantId ?? userInfo.tenantId, userId ?? userInfo.userId),
    ).top(pageSize || this.defaultPageSize);

    request.query(searchExpression.plainSearchText || '');

    if (searchFilterText) {
      request.filter(searchFilterText);
    } else {
      request.orderby(sortExpression || this.defaultSortExpression);
    }
    return new ContactsDataProvider(
      this.baseHttpService,
      request,
      pageSize || this.defaultPageSize,
      this.logService,
      this.organizationUnitService,
      this.authService,
    );
  }
  public getContactsInfoByQuery(
    searchExpression?: SearchParserResult,
    pageSize?: number,
    tenantId?: string,
    userId?: string,
  ): ContactsDataProvider {
    const userInfo = this.authService.getUserInfo();
    const request = new ODataRequest(
      ContactsApiRoutes.Contacts.SearchByUserId(tenantId ?? userInfo.tenantId, userId ?? userInfo.userId),
    ).top(pageSize || this.defaultPageSize);
    request.query(searchExpression.plainSearchText || '');
    request.orderby(this.defaultSortExpression);

    return new ContactsDataProvider(
      this.baseHttpService,
      request,
      pageSize || this.defaultPageSize,
      this.logService,
      this.organizationUnitService,
      this.authService,
    );
  }
  public getContactsByIds(ids: string[]): Observable<IContact[]> {
    const userInfo = this.authService.getUserInfo();
    const request = new ODataRequest(ContactsApiRoutes.Contacts.SearchByUserId(userInfo.tenantId, userInfo.userId));

    const filter = ids.map((id) => GraphFilter.equal('id', id)).join(' or ');

    if (filter) {
      const tenantId = this.organizationUnitService.getTenantId();
      const urlParts = request.getUrlParts();
      request.filter(filter);

      return this.baseHttpService.get(urlParts.baseUrl, { ...urlParts.queryParams, tenantId }).pipe(
        map((data) => data.map((d) => this.mapper.handle(d, { tenantId: [tenantId] }, userInfo))),
        catchError((err) => {
          this.logService.exception(err);
          return of([]);
        }),
      );
    }

    return of([]);
  }

  private composeContactFilter(searchQuery: SearchParserResult): string {
    const commonFilters = this.composeCommonContactFilter(searchQuery.plainSearchText);
    const customFilters = this.composeCustomFilters(searchQuery, customContactSearchQueryFactories);

    const commonFilterText = commonFilters.length > 0 ? `${commonFilters.filter((f) => !!f).join(' or ')}` : null;
    const customFilterText = customFilters.length > 0 ? `${customFilters.filter((f) => !!f).join(' and ')}` : null;

    return commonFilterText && customFilterText ? `(${commonFilterText}) and (${customFilterText})` : commonFilterText || customFilterText;
  }

  private composeCommonContactFilter(searchText: string) {
    const commonFilters: string[] = [];
    if (searchText && searchText !== '') {
      commonFilters.push(
        ...Object.keys(customContactsUserSearchOptions).map((key) => {
          const field = customContactsUserSearchOptions[key];
          return this.composeField(field, searchText);
        }),
      );
    }
    return commonFilters;
  }

  private composeCustomFilters(searchQuery: SearchParserResult, ids: SearchExpressionQueryIds): string[] {
    return searchQuery.searchOptions.reduce((result, currentOption) => {
      if (!searchQuery.hasError(currentOption) && searchQuery.getSearchExpression(currentOption)) {
        const field = customContactsUserSearchOptions[ids[currentOption]];
        const composedSearch = this.composeField(field, searchQuery.getSearchExpression(currentOption));
        if (composedSearch) {
          result.push(composedSearch);
        }
      }
      return result;
    }, []);
  }

  private composeField(field: ODataField, searchText: string): string {
    if (!field) {
      return '';
    }
    switch (field.type) {
      case ODataFieldType.Array:
        return GraphFilter.find(field.root, field.name, searchText, 'contains', [graphFieldModifiers.toLower]);
      case ODataFieldType.ArrayPrimitive:
        return GraphFilter.findPrimitive(field.name, searchText, 'contains', [graphFieldModifiers.toLower]);
      case ODataFieldType.Primitive:
        return GraphFilter.contains(field.name, searchText, [graphFieldModifiers.toLower]);
      default:
        return '';
    }
  }
}
